import { Box, FormControl, MenuItem, Paper, Select, Typography } from "@mui/material"
import { KeyboardArrowDownOutlined } from "@mui-symbols-material/w300"
import { useParams } from "@tanstack/react-router"
import type { FC } from "react"
import { useMemo, useState } from "react"

import { CarrierPreferences } from "./components/carrier/CarrierPreferences"
import { DeliveryAndPackaging } from "./components/DeliveryAndPackaging"
import { MaterialRequirements } from "./components/MaterialRequirements"
import { ShipFromTransitTime } from "./components/ShipFromTransitTime"

import CommentsSection from "@/components/Comment/CommentsSection"
import { FIELD_LABEL_CLASSES, MENU_MAX_HEIGHT } from "@/constants/forms"
import { CommentParentTableType } from "@/graphql/codegen/graphql"
import { useGetLocations } from "@/hooks/useGetLocations"
import { COMPANY_EDIT_ROUTE } from "@/screens/Companies/constants"
import { AddressFormat } from "@/types/common"
import { formatLocationAddress } from "@/utils/utils"

export const CompanyPreferencesTab: FC = () => {
  const { companyId } = useParams({ from: COMPANY_EDIT_ROUTE })
  const { locations, isLocationsFetching } = useGetLocations(companyId)

  const [selectedLocationId, setSelectedLocationId] = useState<string>("")

  // Auto-select first location when available
  useMemo(() => {
    if (locations.length > 0 && !selectedLocationId) {
      setSelectedLocationId(locations[0].locationId)
    }
  }, [locations, selectedLocationId])

  const getLocationLabel = (locationId: string) => {
    const location = locations.find((loc) => loc.locationId === locationId)
    if (!location) return ""
    return formatLocationAddress(location, AddressFormat.Medium)
  }

  if (isLocationsFetching) {
    return (
      <Box className='flex h-full items-center justify-center'>
        <Typography>Loading locations...</Typography>
      </Box>
    )
  }

  if (!locations.length) {
    return (
      <Box className='flex h-full items-center justify-center'>
        <Typography className='text-gray-500'>No locations available. Please add a location first.</Typography>
      </Box>
    )
  }

  return (
    <Box className='flex flex-col gap-6'>
      <FormControl sx={{ maxWidth: 320 }}>
        <label htmlFor='location-selector' className={FIELD_LABEL_CLASSES}>
          Ship To Location
        </label>
        <Select
          id='location-selector'
          value={selectedLocationId}
          onChange={(e) => setSelectedLocationId(e.target.value)}
          displayEmpty
          IconComponent={KeyboardArrowDownOutlined}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: MENU_MAX_HEIGHT,
                minWidth: "max-content",
              },
            },
          }}
          className='mt-1'
          SelectDisplayProps={{
            className: "h-6 px-4 py-2 text-sm leading-5",
          }}
          classes={{
            icon: "text-gray-600",
          }}
        >
          {locations.map((location) => (
            <MenuItem key={location.locationId} value={location.locationId}>
              {getLocationLabel(location.locationId)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box className='flex gap-6'>
        <Box className='flex w-1/2 flex-col'>
          <CarrierPreferences locationId={selectedLocationId} />
        </Box>

        <Box className='flex w-1/2 flex-col gap-6'>
          <ShipFromTransitTime locationId={selectedLocationId} />
          <DeliveryAndPackaging locationId={selectedLocationId} />
          <MaterialRequirements locationId={selectedLocationId} />
        </Box>
      </Box>

      <Paper className='border border-gray-300 p-6' elevation={0}>
        <Typography variant='h6' className='mb-6'>
          Comments
        </Typography>
        <CommentsSection type={CommentParentTableType.CompanyPreferences} referenceId={companyId} />
      </Paper>
    </Box>
  )
}
